/**
 * P5.3 / P5.8 — the save control (recipe page AND quick view sheet).
 *
 * One implementation for both surfaces. The markup, the strings and the state
 * come from `snippets/recipe-save-button.liquid`, which renders the button, its
 * `data-string-*` attributes and the `data-recipe-save-data` blob; this file
 * only moves the state. That matters most for the quick view: its HTML is
 * fetched on demand, so the initial `aria-pressed` / `data-signed-in` are
 * decided by Liquid inside the fetched response and this script binds whatever
 * markup the sheet injected.
 *
 * The initial state is rendered by Liquid (`aria-pressed` = whether this
 * product is in `customer.metafields.recipe.saved`), so a page makes NO request
 * at load and the button cannot be wrong for a signed-in customer before
 * JavaScript runs. The only requests this file makes are the one the shopper
 * asks for by tapping, and a one-shot merge described below.
 *
 * Signed out: the tap cannot be stored server side (the save service identifies
 * the customer from the app-proxy signature only), so the intent is parked in
 * sessionStorage and the shopper is sent to Shopify's new-customer-accounts
 * sign-in with `return_to` pointing back at the recipe. When they come back
 * signed in, the parked intent is POSTed once and cleared. A failed merge keeps
 * the parked entry and leaves the button ready to retry, so nothing is lost.
 *
 * `return_to` is the recipe URL and not the collection the sheet was opened
 * from: the merge runs when the save control is bound on a page load, which
 * only happens on the recipe page (the sheet's markup does not exist until it
 * is fetched), and the recipe page is also the page that shows the shopper the
 * recipe they just saved. Sending them to the collection would leave the intent
 * parked until they happened to open that recipe again.
 *
 * Optimistic UI: the button flips first, then the request runs. Any non-success
 * flips it back to the state the server still has. `full` (128 saved) shows the
 * 128 message with a link to the saved list and does NOT change the list;
 * `error` / network failure offers "Try again". While a request is in flight the
 * button is disabled, and focus is restored afterwards if the shopper had it.
 *
 * The confirmed state is also kept in memory per product for the life of the
 * page (`states`), because the quick view caches the fetched HTML: without it a
 * sheet reopened after a save would paint the state it was fetched in. The
 * recipe page still trusts Liquid — the cache is only written by a 200 from the
 * service and is empty on every new page load.
 *
 * Announcements go through an always-present, visually hidden live region: a
 * region that is `display: none` while empty is not reliable for screen
 * readers, and the visible error block is a normal block of page content that
 * appears and disappears. Both are in `snippets/recipe-save-feedback.liquid`.
 *
 * Every word comes from the JSON blob / `data-string-*` attributes rendered by
 * the snippet (locales/*.json), never from this file.
 */
(function () {
  'use strict';

  /* The recipe page emits this file from sections/main-recipe.liquid AND from
     sections/recipe-related.liquid (both are on the page, and the related strip
     can open the sheet). The second tag must not run a second copy: two copies
     would keep two `states` maps and could bind a sheet to the older module.
     Same guard the quick view script uses for the same reason. */
  if (typeof window.LiberRecipeSave !== 'undefined') {
    return;
  }

  /* Key for the parked signed-out intent. Not a user-facing string. */
  var STORAGE_KEY = 'liber-recipe-save-pending';

  /* Marks a button this file has already bound. The sheet re-injects its HTML
     on every open, so binding must be idempotent per node. */
  var BOUND_ATTR = 'data-recipe-save-bound';

  /* productId -> state confirmed by the service during this page session. */
  var states = {};

  function findIn(scope, attr) {
    return scope.querySelector('[' + attr + ']');
  }

  function bind(button) {
    if (!button || button.getAttribute(BOUND_ATTR) === 'true') {
      return;
    }
    button.setAttribute(BOUND_ATTR, 'true');

    /* Every query below is scoped to the container that owns this control, so
       the recipe page and a quick view sheet (or a sheet re-rendered for
       another recipe) can never read each other's strings or state. */
    var scope = button.closest('[data-recipe-save-scope]') || document;
    var dataEl = findIn(scope, 'data-recipe-save-data');
    if (!dataEl) {
      return;
    }

    var data = {};
    try {
      data = JSON.parse(dataEl.textContent) || {};
    } catch (error) {
      return;
    }

    var strings = {
      save: button.getAttribute('data-string-save') || '',
      saved: button.getAttribute('data-string-saved') || '',
      saveShort: button.getAttribute('data-string-save-short') || '',
      savedShort: button.getAttribute('data-string-saved-short') || '',
      error: button.getAttribute('data-string-error') || '',
      full: button.getAttribute('data-string-full') || '',
      rateLimited: button.getAttribute('data-string-rate-limited') || '',
      viewSaved: button.getAttribute('data-string-view-saved') || ''
    };
    var label = button.querySelector('[data-recipe-save-label]');
    var live = findIn(scope, 'data-recipe-save-status');
    var messageBox = findIn(scope, 'data-recipe-save-message-box');
    var messageText = findIn(scope, 'data-recipe-save-message');
    var messageLink = findIn(scope, 'data-recipe-save-link');
    var retryButton = findIn(scope, 'data-recipe-save-retry');

    var productKey = String(data.productId);
    var saved = data.saved === true;
    if (Object.prototype.hasOwnProperty.call(states, productKey)) {
      saved = states[productKey] === true;
    }
    var busy = false;
    var refocusAfter = false;

    function render() {
      var pressed = saved ? 'true' : 'false';
      if (button.getAttribute('aria-pressed') !== pressed) {
        button.setAttribute('aria-pressed', pressed);
      }
      var name = saved ? strings.saved : strings.save;
      if (name && button.getAttribute('aria-label') !== name) {
        button.setAttribute('aria-label', name);
      }
      var text = saved ? strings.savedShort : strings.saveShort;
      if (label && text) label.textContent = text;
      if (saved) {
        button.setAttribute('data-saved', 'true');
      } else {
        button.removeAttribute('data-saved');
      }
    }

    function clearMessage() {
      if (live) live.textContent = '';
      if (messageText) messageText.textContent = '';
      if (messageLink) {
        messageLink.hidden = true;
        messageLink.removeAttribute('href');
        messageLink.textContent = '';
      }
      if (retryButton) retryButton.hidden = true;
      if (messageBox) messageBox.hidden = true;
    }

    function setMessage(message, options) {
      var settings = options || {};
      var linkUrl = settings.linkUrl || '';
      if (live) {
        live.textContent = linkUrl && settings.linkText ? message + ' ' + settings.linkText : message;
      }
      if (!messageBox) return;
      if (messageText) messageText.textContent = message || '';
      if (messageLink) {
        if (settings.linkUrl) {
          messageLink.textContent = settings.linkText || '';
          messageLink.setAttribute('href', settings.linkUrl);
          messageLink.hidden = false;
        } else {
          messageLink.hidden = true;
          messageLink.removeAttribute('href');
          messageLink.textContent = '';
        }
      }
      if (retryButton) retryButton.hidden = !settings.retry;
      messageBox.hidden = !message;
    }

    function setBusy(next) {
      busy = next;
      if (next) {
        /* A native `disabled` button drops focus; remember whether the shopper
           was on it so the ring can be put back when the request settles. */
        refocusAfter = document.activeElement === button;
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        if (refocusAfter && document.activeElement !== button) {
          try {
            button.focus({ preventScroll: true });
          } catch (error) {
            button.focus();
          }
        }
        refocusAfter = false;
      }
    }

    function productGid(productId) {
      return 'gid://shopify/Product/' + String(productId);
    }

    function listHas(list, productId) {
      if (!Array.isArray(list)) return false;
      var gid = productGid(productId);
      var bare = String(productId);
      for (var i = 0; i < list.length; i++) {
        var entry = String(list[i]);
        if (entry === gid || entry === bare) return true;
      }
      return false;
    }

    function request(path, productId) {
      return fetch(String(data.endpoint || '') + path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'content-type': 'application/json',
          accept: 'application/json'
        },
        body: JSON.stringify({ productId: String(productId) })
      }).then(
        function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (payload) {
              return { status: response.status, payload: payload || {} };
            });
        },
        function () {
          return { status: 0, payload: {} };
        }
      );
    }

    function readPending() {
      try {
        var raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        var parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        if (parsed.productId == null) return null;
        return parsed;
      } catch (error) {
        return null;
      }
    }

    function clearPending() {
      try {
        window.sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        /* private mode / storage disabled: nothing to clear */
      }
    }

    function parkAndSignIn() {
      try {
        window.sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            handle: data.handle || '',
            productId: String(data.productId),
            savedAt: Date.now()
          })
        );
      } catch (error) {
        /* storage unavailable: still send the shopper to sign in so the next
           tap can save (the button simply has nothing parked to merge) */
      }
      if (data.loginUrl) window.location.assign(data.loginUrl);
    }

    /* A 401 means the session was not (or no longer) recognised by the save
       service: park the intent and sign in, exactly like a signed-out tap. The
       merge path never redirects, so a persistent 401 there shows a retry
       instead of bouncing the shopper between two pages. */
    function fail(result, wantRedirect) {
      var payload = result.payload || {};
      var code = payload.code;
      if (result.status === 401 || code === 'unauthorized') {
        if (wantRedirect) {
          parkAndSignIn();
          return;
        }
        setMessage(strings.error, { retry: true });
        return;
      }
      if (code === 'full') {
        setMessage(strings.full, {
          linkText: strings.viewSaved,
          linkUrl: data.savedUrl || payload.saved_path || '',
          retry: false
        });
        return;
      }
      if (result.status === 429 || code === 'rate_limited') {
        setMessage(strings.rateLimited, { retry: true });
        return;
      }
      setMessage(strings.error, { retry: true });
    }

    function toggle() {
      if (busy) return;
      if (!data.signedIn) {
        parkAndSignIn();
        return;
      }

      var wanted = !saved;
      saved = wanted; /* optimistic */
      clearMessage();
      render();
      setBusy(true);

      request(wanted ? '/save' : '/unsave', data.productId).then(function (result) {
        setBusy(false);
        var payload = result.payload || {};
        if (result.status === 200 && payload.ok) {
          saved = Array.isArray(payload.saved) ? listHas(payload.saved, data.productId) : wanted;
          states[productKey] = saved;
          render();
          var pending = readPending();
          if (pending && String(pending.productId) === String(data.productId)) clearPending();
          return;
        }
        saved = !wanted; /* revert to what the server still has */
        states[productKey] = saved;
        render();
        fail(result, true);
      });
    }

    /* One-shot merge of an intent parked before sign-in. Only for this product:
       `return_to` brings the shopper back to the recipe they tapped, so a parked
       entry for another product stays put until that recipe is opened. */
    function mergePending() {
      var pending = readPending();
      if (!pending) return;
      if (String(pending.productId) !== String(data.productId)) return;
      if (!data.signedIn) return;
      if (saved) {
        clearPending();
        return;
      }

      saved = true;
      render();
      setBusy(true);

      request('/save', pending.productId).then(function (result) {
        setBusy(false);
        var payload = result.payload || {};
        if (result.status === 200 && payload.ok) {
          saved = Array.isArray(payload.saved) ? listHas(payload.saved, data.productId) : true;
          states[productKey] = saved;
          render();
          clearPending();
          return;
        }
        saved = false;
        states[productKey] = saved;
        render();
        fail(result, false);
        /* The parked entry stays, so the next tap (or the retry button)
           finishes the job without the shopper having to redo the sign-in. */
      });
    }

    button.addEventListener('click', toggle);
    if (retryButton) {
      retryButton.addEventListener('click', function () {
        clearMessage();
        toggle();
      });
    }

    render();
    mergePending();
  }

  /**
   * Bind every save control under `root` (default: the document). Safe to call
   * repeatedly and on markup that was just injected — the quick view calls it
   * after each render, which is what wires the sheet's control.
   */
  function init(root) {
    var container = root || document;
    var buttons = container.querySelectorAll('[data-recipe-save]');
    for (var i = 0; i < buttons.length; i++) {
      bind(buttons[i]);
    }
  }

  window.LiberRecipeSave = { init: init, states: states };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
